import { useEffect, useRef, useState } from "react";
import {
  Timestamp,
  doc,
  getFirestore,
  onSnapshot,
  onSnapshotsInSync,
} from "firebase/firestore";

export type LagStatus = "ok" | "degraded" | "stale" | "offline";

const OK_THRESHOLD_MS = 5_000;
const DEGRADED_THRESHOLD_MS = 30_000;

const getInitialOnlineState = () =>
  typeof navigator !== "undefined" ? navigator.onLine : true;

export function useFirestoreLag() {
  const [lagMs, setLagMs] = useState<number | null>(null);
  const [fromCache, setFromCache] = useState<boolean | null>(null);
  const [hasPendingWrites, setHasPendingWrites] = useState<boolean>(false);
  const [lastInSyncAt, setLastInSyncAt] = useState<number | null>(null);
  const [status, setStatus] = useState<LagStatus>(
    getInitialOnlineState() ? "ok" : "offline"
  );

  const lagRef = useRef<number | null>(null);
  const fromCacheRef = useRef<boolean | null>(null);
  const onlineRef = useRef<boolean>(getInitialOnlineState());

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const db = getFirestore();
    const heartbeatRef = doc(db, "temp", "heartbeat");
    let disposed = false;

    const recomputeStatus = () => {
      if (disposed) return;
      if (!onlineRef.current) {
        setStatus("offline");
        return;
      }

      const currentLag = lagRef.current;
      const currentFromCache = fromCacheRef.current;

      if (currentLag == null) {
        return;
      }

      if (currentLag < OK_THRESHOLD_MS && currentFromCache === false) {
        setStatus("ok");
      } else if (currentLag < DEGRADED_THRESHOLD_MS) {
        setStatus("degraded");
      } else {
        setStatus("stale");
      }
    };

    const handleOnline = () => {
      onlineRef.current = true;
      recomputeStatus();
    };

    const handleOffline = () => {
      onlineRef.current = false;
      setStatus("offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const unsubscribeHeartbeat = onSnapshot(
      heartbeatRef,
      { includeMetadataChanges: true },
      (snapshot) => {
        if (!snapshot.exists()) {
          return;
        }

        const { fromCache: isFromCache, hasPendingWrites } = snapshot.metadata;
        const serverTime = snapshot.get("serverTime") as Timestamp | undefined;

        setFromCache(isFromCache);
        setHasPendingWrites(hasPendingWrites);
        fromCacheRef.current = isFromCache;

        if (!serverTime) {
          return;
        }

        const now = Date.now();
        const serverMillis = serverTime.toMillis();
        const computedLag = Math.max(0, now - serverMillis);

        lagRef.current = computedLag;
        setLagMs(computedLag);
        recomputeStatus();
      }
    );

    const unsubscribeSync = onSnapshotsInSync(db, () => {
      const now = Date.now();
      setLastInSyncAt(now);
    });

    const intervalId = window.setInterval(recomputeStatus, 1_000);

    return () => {
      disposed = true;
      unsubscribeHeartbeat();
      unsubscribeSync();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.clearInterval(intervalId);
    };
  }, []);

  return {
    status,
    lagMs,
    fromCache,
    hasPendingWrites,
    lastInSyncAt,
  };
}
